<template>
  <div>
    <v-select
        class="hidden-md-and-up mt-6"
        v-model="computedMobileSortModel"
        :items="computedMobileSorts"
        item-text="text"
        item-value="value"
        dense
        rounded
        filled
        hide-details
        prepend-inner-icon="mdi-sort"
    ></v-select>

    <v-data-table
        :key="options.groupBy[0]"
        class="mt-4 table-monitors pi-4"
        dense
        :search="search"
        :options.sync="options"
        v-model="rows.selected"
        :custom-sort="customSort"
        :headers="computedHeaders"
        :items="computedRows"
        item-key="id"
        :item-class="getRowClass"
        :show-select="$vuetify.breakpoint.mdAndUp"
        :footer-props="{ 'items-per-page-options': [50, 100, 200, 500, 1000] }"
        :mobile-breakpoint="0"
        @click:row="handleClick"
    >
      <template v-slot:group.header="{ headers, group, items }">
        <td :colspan="computedVariationPosition" class="text-start" @click="handleClickSearch(group)">
          <v-simple-checkbox
              v-show="$vuetify.breakpoint.mdAndUp"
              :ripple="false"
              :indeterminate="getGroupCheckboxStatus(group) === null"
              :value="getGroupCheckboxStatus(group)"
              style="display: inline; top: 4px; margin-right: 6px;"
              @click="handleGroupCheckboxClick(group)">
          </v-simple-checkbox>
          <span style="line-height: 32px;">
            <span v-if="options.groupBy[0] === 'domain'" class="font-weight-bold">{{ group }}</span>
            <span v-else-if="options.groupBy[0] === 'tags'">
              <span v-if="items[0].tags_entities.length === 0">-</span>
              <span v-else>
                <v-chip
                    v-for="tag in [...items[0].tags_entities].sort((a, b) => a.name.localeCompare(b.name))"
                    :key="tag.id"
                    :color="tag.color"
                    :dark="!$helpers.color.isLight(tag.color)"
                    small
                    class="ma-1"
                    @click.stop.prevent="handleClickSearch(tag.name)"
                >
                  {{ tag.name }}
                </v-chip>
              </span>
            </span>
            <span v-else-if="options.groupBy[0] === 'geolang'">
              <geo-lang-name :country="items[0].country" :language="items[0].language"></geo-lang-name>
            </span>
          </span>
        </td>
        <td :colspan="1" class="text-center" @click="handleClickSearch(group)">
          <span class="font-weight-bold success--text mr-1" style="white-space: nowrap;">
            <v-icon color="success" class="d-xxs-none" small>mdi-arrow-top-right</v-icon> {{
              getWebsiteMonitorsCount(group, 'up')
            }}</span>
          <span class="font-weight-bold error--text" style="white-space: nowrap;">
            <v-icon color="error" class="d-xxs-none" small>mdi-arrow-bottom-right</v-icon> {{
              getWebsiteMonitorsCount(group, 'down')
            }}</span>
        </td>
        <td :colspan="headers.length - 3 + ($vuetify.breakpoint.mdAndUp ? 0 : 1)" @click="handleClickSearch(group)"></td>
      </template>
      <template v-slot:item.data-table-select="{ isSelected, select }">
        <v-simple-checkbox :ripple="false" :value="isSelected" @input="select($event)"></v-simple-checkbox>
      </template>
      <template v-slot:item.favorite="{ item }">
        <span
            v-if="item.favorite"
            type="button"
            class="v-btn v-btn--icon v-btn--round theme--light v-size--small yellow--text text--darken-3"
            @click.stop.prevent="item.favorite = false; $repos.monitors.setFavorite(item.id, false)">
          <span class="v-btn__content">
            <i aria-hidden="true" class="v-icon notranslate mdi mdi-star theme--light"></i>
          </span>
        </span>
        <span
            v-else
            type="button"
            class="v-btn v-btn--icon v-btn--round theme--light v-size--small grey--text"
            @click.stop.prevent="item.favorite = true; $repos.monitors.setFavorite(item.id, true)">
          <span class="v-btn__content">
            <i aria-hidden="true" class="v-icon notranslate mdi mdi-star-outline theme--light"></i>
          </span>
        </span>
      </template>
      <template v-slot:item.domain="{ item }">
        <span style="font-weight: 500">{{ item.domain }}</span>
      </template>
      <template v-slot:item.term="{ item }">
        <v-tooltip bottom v-if="$vuetify.breakpoint.xs">
          <template v-slot:activator="{ on, attrs }">
            <span :class="item.favorite ? 'yellow--text text--darken-3 font-weight-bold' : ''" v-bind="attrs" v-on="on">{{
                item.term
              }}</span>
          </template>
          <span>{{ item.term }}</span>
        </v-tooltip>
        <span v-else>{{ item.term }}</span>
      </template>
      <template v-slot:item.tags="{ item }">
        <v-edit-dialog
            large
            @open="dialog.value = item.tags_entities"
            @save="$repos.monitors.update(item.id, { tags: dialog.value })"
            :cancel-text="$t('actions.cancel')"
            :save-text="$t('actions.save')"
        >
          <div v-if="item.tags_entities.length === 0">
            - <edit-dialog-pencil></edit-dialog-pencil>
          </div>
          <div v-else>
            <div>
              <v-chip
                  v-for="tag in [...item.tags_entities].sort((a, b) => a.name.localeCompare(b.name))"
                  :key="tag.id"
                  :color="tag.color"
                  :dark="!$helpers.color.isLight(tag.color)"
                  small
                  class="ma-1"
                  @click.stop.prevent="handleClickSearch(tag.name)"
              >
                {{ tag.name }}
              </v-chip>
              <edit-dialog-pencil style="line-height: 30px;"></edit-dialog-pencil>
            </div>
          </div>
          <template v-slot:input>
            <v-row style="margin: 0">
              <v-col>
                <tags-select class="mb-3" :value.sync="dialog.value" :closeOnContentClick="true"></tags-select>
              </v-col>
            </v-row>
          </template>
        </v-edit-dialog>
      </template>
      <template v-slot:item.geolang="{ item }">
        <geo-lang-name :country="item.country" :language="item.language"></geo-lang-name>
      </template>
      <template v-slot:item.kwplanner_volume="{ item }">
        <span v-if="item.kwplanner_volume === null">-</span>
        <span v-else>{{ $helpers.present.number(item.kwplanner_volume) }}</span>
      </template>
      <template v-slot:item.best_position="{ item }">
        <span v-if="item.best_position === null">-</span>
        <span v-else>{{ item.best_position }}</span>
      </template>
      <template v-slot:item.position="{ item }">
        <position :value="item.position">
        </position>
      </template>
      <template v-slot:item.last_position="{ item }">
        <position
            :value="item.last_position"
            :hide-loading="true">
        </position>
      </template>
      <template v-slot:item.variation="{ item }">
        <position-variation :value="item.variation"></position-variation>
      </template>
      <template v-slot:item.actions="{  }">
        <span type="button" class="v-btn v-btn--icon v-btn--round theme--light v-size--small primary--text">
          <span class="v-btn__content">
            <i aria-hidden="true" class="v-icon notranslate mdi mdi-magnify theme--light"></i>
          </span>
        </span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import PositionVariation from './Common/PositionVariation'
import Position from './Common/Position'
import GeoLangName from '@/views/Common/Components/GeoLang/GeoLangName'
import TagsSelect from '@/views/Common/Components/Selects/TagsSelect'
import EditDialogPencil from '@/views/Common/Components/Table/EditDialogPencil'

export default {
  name: 'PageTable',
  components: { EditDialogPencil, TagsSelect, GeoLangName, Position, PositionVariation },
  props: ['date', 'reports', 'monitors', 'search', 'group'],
  data () {
    return {
      persist: ['options'],
      options: {
        page: 1,
        itemsPerPage: 200,
        sortBy: ['variation'],
        sortDesc: [false],
        groupBy: [this.group],
        groupDesc: [true],
        mustSort: true,
        multiSort: false
      },
      rows: {
        selected: [],
      },
      dialog: {
        value: null,
      },
      headers: [
        { visible: 'sm', text: '', value: 'favorite', width: '0', sortable: false },
        { visible: 'xs', text: this.$t('domain'), value: 'domain' },
        { visible: 'xs', text: this.$t('term'), value: 'term' },
        { visible: 'sm', text: this.$t('tags'), value: 'tags' },
        { visible: 'xs', text: [this.$t('variation-min'), this.$t('variation')], value: 'variation', align: 'center' },
        { visible: 'xs', text: [this.$t('position-min'), this.$t('position')], value: 'position', align: 'center' },
        { visible: 'xs', text: [this.$t('previous-min'), this.$t('actions.previous')], value: 'last_position', align: 'center' },
        { visible: 'sm', text: this.$t('best'), value: 'best_position', align: 'center' },
        { visible: 'md', text: this.$t('geolang'), value: 'geolang' },
        { visible: 'md', text: this.$t('volume'), value: 'kwplanner_volume', align: 'right' },
        { visible: 'md', text: '', value: 'actions', cellClass: 'actions', sortable: false, align: 'right' }
      ]
    }
  },
  watch: {
    computedSelectedRowsIds (value) {
      this.$emit('select', value)
    },
    group (value) {
      this.options.groupBy = [value]
    }
  },
  computed: {
    computedCurrentSample () {
      return this.$services.monitoring.computedCurrentSample
    },
    computedComparisonSample () {
      return this.$services.monitoring.computedComparisonSample
    },
    computedHeaders () {
      let weights = { 'xs': 1, 'sm': 2, 'md': 3, 'lg': 4, 'xl': 5 }
      return this.headers.filter(header => {
        return weights[header.visible] <= weights[this.$vuetify.breakpoint.name]
      }).map(header => {
        if (Array.isArray(header.text)) {
          return {
            ...header,
            text: header.text[this.$vuetify.breakpoint.name === 'xs' ? 0 : 1]
          }
        } else {
          return header
        }
      })
    },
    computedMobileSorts () {
      const sorts = []
      this.headers.forEach(header => {
        if (header.visible && header.sortable !== false) {
          sorts.push({
            text: (Array.isArray(header.text) ? header.text[1] : header.text) + ' (asc)',
            value: { column: header.value, desc: false }
          })
          sorts.push({
            text: (Array.isArray(header.text) ? header.text[1] : header.text) + ' (desc)',
            value: { column: header.value, desc: true }
          })
        }
      })
      return sorts
    },
    computedMobileSortModel: {
      get () {
        return {
          column: this.options.sortBy[0],
          desc: this.options.sortDesc[0]
        }
      },
      set (value) {
        this.$set(this.options, 'sortBy', [value.column])
        this.$set(this.options, 'sortDesc', [value.desc])
      }
    },
    computedSelectedRowsIds () {
      return this.rows.selected.map(selected => {
        return selected.id
      })
    },
    computedRows () {
      const monitors = this.monitors.slice(0, this.$account.current.rules['check-position'].max_monitor)
      // const monitors = this.monitors

      return monitors.map(row => {
        let position = undefined
        // If there is a report with samples for today
        if (this.reports.current) {
          // If the report includes the id of the monitor
          if (row.id in this.reports.current.monitors) {
            // If the selected sample is on the list of positions of the monitor
            if (this.computedCurrentSample in this.reports.current.monitors[row.id]) {
              position = this.reports.current.monitors[row.id][this.computedCurrentSample]
            }
          }
        }

        let lastPosition = undefined
        // If there is a report with samples for today
        if (this.reports.comparison) {
          // If the report includes the id of the monitor
          if (row.id in this.reports.comparison.monitors) {
            // If the selected sample is on the list of positions of the monitor
            if (this.computedComparisonSample in this.reports.comparison.monitors[row.id]) {
              lastPosition = this.reports.comparison.monitors[row.id][this.computedComparisonSample]
            }
          }
        }

        if (parseInt(this.computedCurrentSample) > 0 && position === undefined) {
          return false
        } else {
          return {
            id: row.id,
            domain: row.website.domain,
            term: row.keyword.term,
            tags: row.tags.map(tag => tag.name).join(', '),
            tags_entities: row.tags,
            variation: this.$models.Position.getVariation({ position, last_position: lastPosition }),
            position,
            last_position: lastPosition,
            best_position: row.best_position,
            geolang: row.keyword.language.toLowerCase() + '-' + row.keyword.country.toUpperCase(),
            country: row.keyword.country,
            language: row.keyword.language,
            kwplanner_volume: row.keyword.kwplanner_volume,
            favorite: row.favorite,
          }
        }
      }).filter(row => row !== false)
    },
    computedVariationPosition () {
      let position = 5

      if(this.options.groupBy.length > 0) {
        if(['domain', 'tags'].includes(this.options.groupBy)) {
          position--
        }
      }

      if(this.$vuetify.breakpoint.smAndDown) {
        position--
      }

      if(this.$vuetify.breakpoint.xsOnly) {
        position--
      }

      return position
    }
  },
  methods: {
    customSort (items, index, isDesc) {
      const groupBy = this.options.groupBy[0]
      index = index[1]
      isDesc = isDesc[1]

      items.sort((a, b) => {
        if (a[groupBy] === b[groupBy]) {
          if(a.favorite !== b.favorite) {
            return !a.favorite ? 1 : -1
          } else if (index === 'variation') {
            if (isDesc) return this.$models.Position.isBelow(a, b)
            else return this.$models.Position.isUpper(a, b)
          } else if (['term', 'tags', 'geolang'].includes(index)) { // For string based columns
            if (isDesc) {
              if (a[index] === '') return 1
              else if (b[index] === '') return -1
              return a[index].localeCompare(b[index])
            }
            else {
              if (b[index] === '') return 1
              else if (a[index] === '') return -1
              return b[index].localeCompare(a[index])
            }
          } else if (index === 'position' || index === 'last_position' || index === 'best_position') {
            const apos = this.$models.Position.getPositionScore(a[index])
            const bpos = this.$models.Position.getPositionScore(b[index])
            if (isDesc) return apos - bpos
            else return bpos - apos
          } else if (index) { // For numeric based columns
            if (isDesc) {
              if (a[index] === null) return 1
              else if (b[index] === null) return -1
              else return b[index] - a[index]
            } else {
              if (b[index] === null) return 1
              else if (a[index] === null) return -1
              else return a[index] - b[index]
            }
          }
        }

        if (a[groupBy] === '') return 1
        else if (b[groupBy] === '') return -1
        else return a[groupBy] > b[groupBy] ? 1 : -1
      })

      this.$emit('sort', items.map(item => item.id))

      return items
    },
    getGroupCheckboxStatus (groupValue) {
      const groupBy = this.options.groupBy[0]
      let checked = 0
      let unchecked = 0

      this.computedRows.forEach(row => {
        if (row[groupBy] === groupValue) {
          const found = this.rows.selected.find(selected => selected.id === row.id)
          if (found) checked++
          else unchecked++
        }
      })

      if (checked > 0 && unchecked > 0) {
        return null
      } else {
        return checked > 0
      }
    },
    handleGroupCheckboxClick (groupValue) {
      const groupBy = this.options.groupBy[0]
      this.computedRows.forEach(row => {
        if (row[groupBy] === groupValue) {
          const found = this.rows.selected.findIndex(selected => selected.id === row.id)
          if (found === -1) {
            this.rows.selected.push(row)
          } else {
            this.rows.selected.splice(found, 1)
          }
        }
      })
    },
    handleClickSearch (search) {
      this.$emit('search', search)
    },
    handleClick (item) {
      this.$ui.openDialog('keyword_detail', {
        item,
        date: this.$services.monitoring.date
      })
    },
    getRowClass (item) {
      if (this.rows.selected.includes(item.id)) {
        return 'v-data-table__expanded__content row-selected'
      } else if (item.position === null) {
        return 'v-data-table__empty-wrapper'
      } else {
        return ''
      }
    },
    getWebsiteMonitorsCount (groupValue, mode) {
      const groupBy = this.options.groupBy[0]
      return this.computedRows.filter(row => {
        return row[groupBy] === groupValue
      }).reduce((total, row) => {
        if (mode === 'up') return total + (this.$models.Position.isGoingUp(row) ? 1 : 0)
        else return total + (this.$models.Position.isGoingDown(row) ? 1 : 0)
      }, 0)
    }
  },
  mounted () {
    this.$bus.on('monitoring/require_refresh', () => this.rows.selected = [])
  }
}
</script>

<style>
/* Remove the checkbox margin right from the table */
.table-monitors .v-input--selection-controls__input {
  margin-right: 0;
}

/* Try to stack kw smaller in order to use a single table line for two line text */
.table-monitors tr > td:first-child {
  line-height: 1em;
}

.table-monitors tr td {
  /* white-space: nowrap; */ /* DISABLE KW ON TWO LINES */
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.table-monitors .actions {
  width: 1%;
  white-space: nowrap;
}

@media screen and (max-width: 960px) {
  .table-monitors td {
    font-size: 0.800rem !important;
  }
}

@media screen and (max-width: 600px) {
  .table-monitors tr td:first-child {
    max-width: 240px;
  }
}

@media screen and (max-width: 400px) {
  .table-monitors tr td:first-child {
    max-width: 180px;
  }
}
</style>

<i18n>
{
	"en": {
		"best": "Best position",
		"domain": "Website",
		"geolang": "Geoloc & Language",
		"position": "Position",
		"position-min": "Pos.",
		"previous-min": "Previous position",
		"tags": "Tags",
		"term": "Keyword",
		"variation": "Variation",
		"variation-min": "Var.",
		"volume": "Volume"
	},
	"es": {
		"best": "Mejor posicion",
		"domain": "Sitio Web",
		"geolang": "Geolocalización y Lenguaje",
		"position": "Posición",
		"position-min": "Pos.",
		"previous-min": "Posición anterior",
		"tags": "Etiquetas",
		"term": "Palabra clave",
		"variation": "Variación",
		"variation-min": "Var.",
		"volume": "Volumen"
	},
	"fr": {
		"best": "Meilleure position",
		"domain": "Site Web",
		"geolang": "Géoloc & Langue",
		"position": "Position",
		"position-min": "Pos.",
		"previous-min": "Préc.",
		"tags": "Tags",
		"term": "Mot-clé",
		"variation": "Variation",
		"variation-min": "Var.",
		"volume": "Volume"
	},
	"it": {
		"best": "Posizione migliore",
		"domain": "Sito web",
		"geolang": "Geoloc & Lingua",
		"position": "Posizione",
		"position-min": "Pos.",
		"previous-min": "Prec.",
		"tags": "Tag",
		"term": "Parola chiave",
		"variation": "Variazione",
		"variation-min": "Var.",
		"volume": "Volume"
	},
	"pt": {
		"best": "Melhor posição",
		"domain": "Website",
		"geolang": "Geoloc & Idioma",
		"position": "Posição",
		"position-min": "Pos.",
		"previous-min": "Ant.",
		"tags": "Tags",
		"term": "Palavra-chave",
		"variation": "Variação",
		"variation-min": "Var.",
		"volume": "Volume"
	},
	"pl": {
		"best": "Najlepsza pozycja",
		"domain": "Strona internetowa",
		"geolang": "Geolok. i Język",
		"position": "Pozycja",
		"position-min": "Poz.",
		"previous-min": "Poprz.",
		"tags": "Tagi",
		"term": "Słowo kluczowe",
		"variation": "Zmiana",
		"variation-min": "Zm.",
		"volume": "Wolumen"
	}
}
</i18n>
